import { memo, useState, useEffect, ChangeEvent } from 'react';

import GreenButton from '../../components/GreenButton';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';

import {
  freqValue,
  freqUnit,
  getFrequencyFromSnapshotIndex,
  getIndexFromSnapshotFrequency,
  getCenterWithLo,
} from '../../utils/FreqUtils';

import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Grid,
  SelectChangeEvent,
} from '@mui/material';

import InfoPopup from '../../components/InfoPopup';

import {
  InvalidEntryFlags,
  ThresholdForm,
  UpdateSnapshotThreshold,
} from './types';
import ThresholdList from './ThresholdList';
import ThresholdSettingsForm from './ThresholdSettingsForm';

import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useThreshold } from './api/getThreshold';
import { useCreateThreshold } from 'features/thresholds/api/createThreshold';
import { useUpdateThreshold } from 'features/thresholds/api/updateThreshold';
import { useDeleteThreshold } from 'features/thresholds/api/deleteThreshold';
import { InterferenceDetector } from 'types/shared/InterferenceDetector';
import DraggablePaper from 'components/DraggablePaper';

interface Props {
  traceLength: number;
  open: boolean;
  handleRedrawThreshold: (
    thresholdId: number,
    detectorType: InterferenceDetector,
    detectorName?: string | undefined
  ) => void;
  handleClose: () => void;
  selectedSnapshotId: number;
  isHistoryMode?: boolean;
}

const invalidEntryFlagsInitialState = {
  begin: false,
  end: false,
  bound: false,
};

const ThresholdConfigurationDialog = memo(
  ({
    traceLength,
    open,
    handleClose,
    handleRedrawThreshold,
    selectedSnapshotId,
    isHistoryMode = false,
  }: Props) => {
    const [hasChanges, setHasChanges] = useState(false);
    const [invalidEntryFlags, setInvalidEntryFlags] =
      useState<InvalidEntryFlags>(invalidEntryFlagsInitialState);

    const [selectedThresholdId, setSelectedThresholdId] = useState(-1);
    const [threshold, setThreshold] = useState<ThresholdForm>({
      name: '',
      beginFrequencyValue: '',
      beginFrequencyUnit: 'MHz',
      endFrequencyValue: '',
      endFrequencyUnit: 'MHz',
      bound: '',
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const { data: snapshot } = useSnapshot({ snapshotId: selectedSnapshotId });
    const { thresholds } = snapshot || {};
    const { data: selectedThreshold } = useThreshold({
      snapshotId: selectedSnapshotId,
      thresholdId: selectedThresholdId,
    });
    const createThresholdMutation = useCreateThreshold({
      snapshotId: selectedSnapshotId,
    });
    const updateThresholdMutation = useUpdateThreshold({
      snapshotId: selectedSnapshotId,
    });
    const deleteThresholdMutation = useDeleteThreshold({
      snapshotId: selectedSnapshotId,
    });

    useEffect(() => {
      if (selectedThresholdId === -1 || !thresholds || !selectedThreshold) {
        setThreshold({
          name: '',
          beginFrequencyValue: '',
          beginFrequencyUnit: 'MHz',
          endFrequencyValue: '',
          endFrequencyUnit: 'MHz',
          bound: '',
        });
        return;
      }

      if (!traceLength || !snapshot) return;

      const beginFrequencyIndex = selectedThreshold.begin;
      const endFrequencyIndex = selectedThreshold.end;
      const centerWithLo = getCenterWithLo(
        snapshot.center,
        snapshot.lo,
        snapshot.lo_mode
      );

      const beginFrequency = getFrequencyFromSnapshotIndex(
        centerWithLo,
        snapshot.span,
        traceLength,
        beginFrequencyIndex
      );
      const endFrequency = getFrequencyFromSnapshotIndex(
        centerWithLo,
        snapshot.span,
        traceLength,
        endFrequencyIndex
      );

      const beginFrequencyValue = freqValue(beginFrequency);
      const beginFrequencyUnit = freqUnit(beginFrequency);

      const endFrequencyValue = freqValue(endFrequency);
      const endFrequencyUnit = freqUnit(endFrequency);

      const parsedThreshold = {
        ...selectedThreshold,
        beginFrequencyValue: beginFrequencyValue,
        beginFrequencyUnit: beginFrequencyUnit,
        endFrequencyValue: endFrequencyValue,
        endFrequencyUnit: endFrequencyUnit,
        bound: selectedThreshold.bound / 1000,
      };

      setThreshold(parsedThreshold);
    }, [
      selectedThresholdId,
      selectedThreshold,
      traceLength,
      snapshot,
      snapshot?.center,
      snapshot?.span,
      thresholds,
    ]);

    const handleThresholdChanged = (
      event:
        | SelectChangeEvent<string>
        | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        checked?: boolean
    ) => {
      const { name, value } = event.target;
      if (name === undefined) return;
      const valueToSet = name === 'warning' ? checked : value;

      setThreshold({
        ...threshold,
        [name]: valueToSet,
      });
      setHasChanges(true);

      setInvalidEntryFlags(invalidEntryFlagsInitialState);
    };

    const handleSubmitClicked = async () => {
      if (!snapshot) return;

      const beginFrequency =
        threshold.beginFrequencyValue + ' ' + threshold.beginFrequencyUnit;
      const endFrequency =
        threshold.endFrequencyValue + ' ' + threshold.endFrequencyUnit;
      const centerWithLo = getCenterWithLo(
        snapshot.center,
        snapshot.lo,
        snapshot.lo_mode
      );

      const beginIndex = getIndexFromSnapshotFrequency(
        centerWithLo,
        snapshot.span,
        traceLength,
        beginFrequency
      );
      const endIndex = getIndexFromSnapshotFrequency(
        centerWithLo,
        snapshot.span,
        traceLength,
        endFrequency
      );

      // Validate inputs and set error flags accordingly
      const newFlags: InvalidEntryFlags = {
        begin: false,
        end: false,
        bound: false,
      };
      if (beginIndex === -1) newFlags.begin = true;
      if (endIndex === -1) newFlags.end = true;
      const graphMax = snapshot.reference;
      const graphMin = -(10 * snapshot.scale) + snapshot.reference;
      if (
        Number(threshold.bound) > graphMax ||
        Number(threshold.bound) < graphMin
      )
        newFlags.bound = true;

      if (newFlags.begin || newFlags.end || newFlags.bound) {
        setInvalidEntryFlags(newFlags);
        return;
      }

      if (beginIndex >= endIndex) {
        setInvalidEntryFlags((flags) => ({ ...flags, begin: true, end: true }));
        return;
      }

      if (threshold.id === undefined || threshold.snapshot_id === undefined)
        return;

      const updateThreshold: UpdateSnapshotThreshold = {
        id: threshold.id,
        snapshot_id: threshold.snapshot_id,
        name: threshold.name,
        begin: beginIndex,
        end: endIndex,
        bound: Number(threshold.bound) * 1000,
        warning: !!threshold.warning,
      };

      await updateThresholdMutation.mutateAsync({
        data: updateThreshold,
        snapshotId: selectedSnapshotId,
        thresholdId: updateThreshold.id,
      });

      setSelectedThresholdId(-1);
      setHasChanges(false);
    };

    const handleDeleteThresholdClicked = async () => {
      if (!selectedThreshold) return;

      await deleteThresholdMutation.mutateAsync({
        snapshotId: selectedSnapshotId,
        thresholdId: selectedThreshold.id,
      });

      setSelectedThresholdId(-1);
    };

    const handleNewThresholdClicked = async () => {
      if (!snapshot) return;
      const newThreshold = {
        snapshot_id: snapshot.id,
        name: 'New Threshold',
        begin: 0,
        end: 0,
        bound: -30 * 1000,
      };

      await createThresholdMutation.mutateAsync({
        data: newThreshold,
        snapshotId: selectedSnapshotId,
      });
    };

    const handleThresholdClicked = (thresholdId: number) => {
      setSelectedThresholdId(thresholdId);
      setInvalidEntryFlags(invalidEntryFlagsInitialState);
    };

    return (
      <>
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          itemToDelete={threshold.name}
          onSubmit={handleDeleteThresholdClicked}
          onClose={() => setDeleteDialogOpen(false)}
        />
        <Dialog
          open={open}
          onClose={handleClose}
          hideBackdrop
          PaperComponent={DraggablePaper}
          aria-labelledby='draggable-dialog-title'
        >
          <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>
            Threshold Configuration
          </DialogTitle>
          <DialogContent>
            <Grid container>
              <Grid item xs={6}>
                <ThresholdList
                  thresholds={thresholds}
                  selectedThresholdId={selectedThresholdId}
                  handleThresholdClicked={handleThresholdClicked}
                />
              </Grid>
              <Grid item xs={6}>
                <ThresholdSettingsForm
                  threshold={threshold}
                  handleThresholdChanged={handleThresholdChanged}
                  invalidEntryFlags={invalidEntryFlags}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSelectedThresholdId(-1);
                handleClose();
                setInvalidEntryFlags(invalidEntryFlagsInitialState);
              }}
              variant='contained'
              color='secondary'
            >
              Cancel
            </Button>
            <Button
              onClick={() => setDeleteDialogOpen(true)}
              color='error'
              variant='contained'
              disabled={selectedThresholdId === -1}
            >
              Delete Threshold
            </Button>
            <Button
              onClick={() =>
                handleRedrawThreshold(
                  selectedThresholdId,
                  InterferenceDetector.Threshold,
                  selectedThreshold?.name
                )
              }
              name='Redraw'
              color='primary'
              variant='contained'
              disabled={selectedThresholdId === -1}
            >
              Redraw
            </Button>
            <GreenButton
              onClick={handleNewThresholdClicked}
              name='New Threshold'
            ></GreenButton>
            <Button
              onClick={handleSubmitClicked}
              color='primary'
              variant='contained'
              disabled={selectedThresholdId === -1 || !hasChanges}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
        <InfoPopup
          openCondition={
            invalidEntryFlags.begin ||
            invalidEntryFlags.end ||
            invalidEntryFlags.bound
          }
          title='Invalid Values'
          message='Your settings could not be saved due to an invalid begin, end, or bound value.'
          error
        />
      </>
    );
  }
);

export default ThresholdConfigurationDialog;
